/**
 * FERP Portal - User Menu Items Hook
 *
 * Backend-driven user menu items for the avatar dropdown.
 * DRR-313: Uses NavigationContext to avoid duplicate queries.
 *
 * FERP Principles:
 * - Backend authority: Items defined in Setting YAML, not frontend code
 * - Scope-gated: Items with required_scopes hidden if user lacks those scopes
 * - NVO-customizable: Per-tenant rename/hide via lever 6
 * - Fail-closed: Empty array while loading (DRR-102/DRR-123)
 */

import { useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';

export interface UserMenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  sortOrder: number;
}

/**
 * Returns scope-filtered user menu items for the avatar dropdown.
 * Uses NavigationContext (DRR-313) to avoid duplicate queries.
 */
export function useUserMenuItems(): {
  items: UserMenuItem[];
  loading: boolean;
} {
  const { settingBundle, userScopes, loading } = useNavigation();

  const items = useMemo(() => {
    const rawItems = settingBundle?.userMenuItems ?? [];
    const hasWildcard = userScopes.has('*') || userScopes.has('*:*');

    return rawItems
      .filter((item) => {
        if (!item.requiredScopes || item.requiredScopes.length === 0) {
          return true;
        }
        if (hasWildcard) {
          return true;
        }
        return item.requiredScopes.some((scope) => userScopes.has(scope));
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({
        id: item.id,
        label: item.label,
        path: item.path,
        icon: item.icon,
        sortOrder: item.sortOrder,
      }));
  }, [settingBundle, userScopes]);

  return { items, loading };
}
